import { CustomLabel8, CustomSelect, CustomText, Label } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { InputMoney } from '@4beefopag/4bee-fopag-core/dist/atoms.es';
import { mostrarErro } from '@4beefopag/4bee-fopag-core/dist/molecules';
import { Page } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { AuthContext } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import { CalendarOutlined, InfoCircleFilled } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MODULE_PAYROLL_ORDER } from '../../../constants/modules';
import { useUserPolicy, Can } from '../../../user-policy';
import { buscarParametrizacao, buscarUltimaParametrizacaoCriada } from '../../../services/parametrizacaoApp';
import * as parametrizacaoRepo from '../../../services/repository/parametrizacaoRepository';
import SelectFolha from '../../atoms/SelectFolha';
import TagsEmail from '../../molecules/TagsEmail';
import * as CompanyRepository from '../../../services/repository/companyRepository';

const { Option } = Select;
const { MonthPicker } = DatePicker;

const API_INTEGRATION_DISABLES_APDATA_SYNC_MESSAGE =
  'Esta empresa utiliza integração via API para o Apontamento de Produção. Por isso, a atualização automática pelo Apdata foi desabilitada automaticamente para evitar conflitos de dados. Se precisar alterar essa configuração, fale com o time de Desenvolvimento.';

const CustomInputNumber = styled(InputNumber)`
  width: unset !important;
`;

const AFASTADOS_COMPETENCIAS_OPTIONS = [
  { value: 0, label: 'Afastados na competência' },
  { value: 1, label: '1 Competência após afastamento' },
  { value: 2, label: '2 Competências após afastamento' },
  { value: 3, label: '3 Competências após afastamento' },
  { value: 4, label: '4 Competências após afastamento' },
  { value: 5, label: '5 Competências após afastamento' },
  { value: 6, label: '6 Competências após afastamento' },
  { value: 7, label: '7 Competências após afastamento' },
  { value: 8, label: '8 Competências após afastamento' },
  { value: 9, label: '9 Competências após afastamento' },
  { value: 10, label: '10 Competências após afastamento' },
  { value: 11, label: '11 Competências após afastamento' },
  { value: 12, label: '12 Competências após afastamento' },
];

function ParametrizacaoApp() {
  const { id: idFolhaParam } = useParams();
  const [searchParams] = useSearchParams();
  const usuario = useContext(AuthContext);
  const { userAccount } = useUserPolicy();
  const canUpdateBlockContract = userAccount.ability.can('update', 'payroll.parameterization.block-contract');
  const initalFieldsData = {
    id: 0,
    nome: '',
    vigencia: undefined,
    adiantamento_auto: false,
    adiantamento_auto_id_tipo: 1,
    adiantamento_auto_valor: 0,
    atual_comp_ap: false,
    atual_comp_ap_qtd_dias: 0,
    apresentar_coop_afast: false,
    apresentar_coop_afast_competencias: 0,
    mensa_propor_remu_admissao: false,
    horis_propor_remu_admissao: false,
    base_hora: 0,
    exibe_param_apontamento: false,
    bloquear_coop_sem_contrato: false,
    email_alerta: undefined,
    email_alerta_adm: undefined,
    email_alerta_financeiro: undefined,
    alerta_pedido_sup_inf: false,
    alerta_pedido_sup_inf_valor: 0,
    ga_should_sync_earning_values: false,
    autoTransmit: true,
  };

  const [fieldsData, setFieldsData] = useState({
    ...initalFieldsData,
    id_folha: undefined,
  });

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const isApiIntegrationEnabled = company?.hasApiIntegration === true;
  const canUpdateSensitiveParam = userAccount.ability.can('update', 'payroll.parameterization.sensitive');
  const canChangeAutoTransmit = Boolean(userAccount?.isAdmin);

  const { isGestor } = usuario;

  useEffect(() => {
    if (idFolhaParam) {
      (async () => {
        const parametrizacao = await parametrizacaoRepo.findById(idFolhaParam);

        parametrizacao.vigencia = moment(
          `${parametrizacao.ano_vigencia}-${parametrizacao.mes_vigencia}-01`,
          'YYYY-MM-DD'
        );

        setFieldsData({ ...parametrizacao });
      })();
    }
  }, [idFolhaParam]);

  useEffect(() => {
    const folhaParams = searchParams.get('folha');
    if (folhaParams) {
      const newValue = { ...initalFieldsData, id_folha: Number(folhaParams) };
      const vigenciaParams = searchParams.get('vigencia');
      if (vigenciaParams) {
        newValue.vigencia = moment(`${vigenciaParams}-01`, 'YYYY-MM-DD');
      }
      setFieldsData(newValue);
      (async () => {
        const companies = await CompanyRepository.getAccessibleCompanies({ modules: [MODULE_PAYROLL_ORDER] });
        setCompany(companies.find((c) => c.id === Number(folhaParams)));
      })();
    }
  }, []);

  useEffect(() => {
    if (company && !fieldsData.bloquear_coop_sem_contrato && !canUpdateBlockContract) {
      setFieldsData({ ...fieldsData, bloquear_coop_sem_contrato: true });
    }
  }, [company]);

  useEffect(() => {
    if (!isApiIntegrationEnabled) {
      return;
    }

    setFieldsData((prev) => {
      if (!prev.ga_should_sync_earning_values) {
        return prev;
      }
      return { ...prev, ga_should_sync_earning_values: false };
    });
  }, [isApiIntegrationEnabled, company?.id]);

  const buscar = async (vigencia) => {
    const [parametrizacao, ultimaParametrizacao] = await Promise.all([
      buscarParametrizacao(fieldsData.id_folha, vigencia),
      buscarUltimaParametrizacaoCriada(fieldsData.id_folha),
    ]);

    if (parametrizacao) {
      parametrizacao.vigencia = moment(
        `${parametrizacao.ano_vigencia}-${parametrizacao.mes_vigencia}-01`,
        'YYYY-MM-DD'
      );
      if (company && !canUpdateBlockContract) {
        parametrizacao.bloquear_coop_sem_contrato = true;
      }
      if (isApiIntegrationEnabled) {
        parametrizacao.ga_should_sync_earning_values = false;
      }

      setFieldsData({ ...parametrizacao });
    } else {
      // para uma nova parametrização, utiliza os valores da ultima criada
      const keyToReplaceValueByLastChange = ['bloquear_coop_sem_contrato'];
      if (ultimaParametrizacao) {
        const parametrizacaoAtualizada = { ...fieldsData };
        for (const key of keyToReplaceValueByLastChange) {
          if (ultimaParametrizacao[key] !== undefined) {
            parametrizacaoAtualizada[key] = ultimaParametrizacao[key];
          }
        }
        if (company) {
          parametrizacaoAtualizada.bloquear_coop_sem_contrato = true;
        }
        if (isApiIntegrationEnabled) {
          parametrizacaoAtualizada.ga_should_sync_earning_values = false;
        }
        setFieldsData({ ...parametrizacaoAtualizada, vigencia });
      }
    }
  };

  const validarCampos = () => {
    let erro = '';
    if (fieldsData.atual_comp_ap && fieldsData.atual_comp_ap_qtd_dias === undefined) {
      erro += 'O campo Autalização da Competência no Apontamento é obrigatório, preencher o Dia \n\n';
    }
    if (fieldsData.base_hora === undefined || fieldsData.base_hora === '' || fieldsData.base_hora === null) {
      erro += 'O campo base hora é obrigatório! \n\n';
    } else if (fieldsData.base_hora === 0) {
      erro += 'Zero não é um valor válido para o campo base hora! \n\n';
    }

    if (
      isGestor() &&
      (fieldsData.email_alerta === undefined || fieldsData.email_alerta === '' || fieldsData.email_alerta === null)
    ) {
      erro += 'O campo de e-mail para alerta ao Facilitador é obrigatório! \n\n';
    }

    if (fieldsData.nome === undefined || fieldsData.nome === '' || fieldsData.nome === null) {
      erro += 'O campo nome é obrigatório! \n\n';
    }

    return erro;
  };

  const salvar = async () => {
    setLoading(true);
    const erros = validarCampos();

    if (erros) {
      setLoading(false);

      Modal.error({
        title: 'Ops...',
        content: erros,
        centered: true,
      });
    } else {
      try {
        const model = { ...fieldsData };
        model.mes_vigencia = moment(model.vigencia).format('MM');
        model.ano_vigencia = moment(model.vigencia).format('YYYY');
        if (isApiIntegrationEnabled) {
          model.ga_should_sync_earning_values = false;
        }

        if (fieldsData.id) {
          await parametrizacaoRepo.update(fieldsData.id, model);
        } else {
          await parametrizacaoRepo.create(model);
        }

        Modal.success({
          className: 'modal-parametrizacao-sucesso',
          centered: true,
          width: 560,
          title: (
            <CustomText
              color="var(--text-grey-8)"
              fontSize={16}
            >
              Parametrização {fieldsData.id === 0 ? 'Cadastrada' : 'Alterada'} com Sucesso
            </CustomText>
          ),
          content: <p>A parametrização entra em vigor a partir de {moment(fieldsData.vigencia).format('MM/YYYY')}.</p>,
        });
        buscar(fieldsData.vigencia);
        setLoading(false);
      } catch (e) {
        mostrarErro(e);
        setLoading(false);
      }
    }
  };

  const onValidacaoEmailBwg = (email) => {
    if (email.includes('@bwg.com.br') || email.includes('@novacoop.com.br')) {
      return true;
    }

    message.error(`O e-mail ${email} não pertence à BWG ou a Nova Cooperativa.`);
    return false;
  };

  const onValidacaoEmail = (email) => {
    if (/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      return true;
    }
    message.error(`O e-mail ${email} não é valido.`);
    return false;
  };

  return (
    <Page
      projeto="Cooperativa"
      titulo="Parametrização"
      linkManual={process.env.REACT_APP_LINK_MANUAL}
    >
      <Row
        type="flex"
        gutter={[16, 16]}
      >
        <Col
          xs={24}
          sm={24}
          md={12}
          lg={7}
          xl={12}
        >
          <SelectFolha
            module={MODULE_PAYROLL_ORDER}
            idFolhaSelecionada={fieldsData.id_folha}
            onChange={(value) => {
              const isSameFolha = value && value.id === fieldsData.id_folha;

              /**
               * IMPORTANTE: Previne um "race condition" e reset indevido do estado.
               *
               * O bug ocorria no seguinte cenário:
               * 1. A página carrega via URL com um ID (ex: /parametrizar/7439).
               * 2. O primeiro useEffect busca os dados na API e atualiza o 'fieldsData' (com ID e vigência).
               * 3. O componente SelectFolha, após carregar suas opções, dispara o 'onChange' internamente para
               *    sincronizar o objeto da folha selecionada.
               * 4. Este 'onChange', ao detectar que 'fieldsData.id' já estava preenchido, executava a lógica de
               *    reset para 'initalFieldsData', apagando a vigência e causando tela branca (devido à
               *    condicional de renderização que depende da vigência).
               *
               * A verificação 'isSameFolha' garante que se a folha não mudou de fato, o estado não seja resetado.
               *
               * @TODO: Devemos melhorar a arquitetura desse componente para evitar esse tipo de bug.
               */
              if (isSameFolha) {
                setCompany(value);
                return;
              }

              if (fieldsData.id) {
                setFieldsData({
                  ...fieldsData,
                  ...initalFieldsData,
                  id_folha: value.id,
                });
              } else if (!value) {
                setFieldsData({
                  ...fieldsData,
                  id_folha: undefined,
                  vigencia: undefined,
                });
              } else {
                setFieldsData({
                  ...fieldsData,
                  id_folha: value.id,
                });
              }
              setCompany(value);
            }}
          />
        </Col>
        <Col
          xs={24}
          sm={24}
          md={12}
          lg={4}
          xl={4}
        >
          <Label label="Data de Vigência">
            <MonthPicker
              value={fieldsData.vigencia}
              onChange={(value) => {
                if (company && !canUpdateBlockContract) {
                  initalFieldsData.bloquear_coop_sem_contrato = true;
                }
                setFieldsData({ ...fieldsData, ...initalFieldsData, vigencia: value });
                buscar(value);
              }}
              suffixIcon={<CalendarOutlined />}
              placeholder="MM/YYYY"
              defaultValue={fieldsData.vigencia}
              format="MM/YYYY"
              allowClear={false}
              disabled={!fieldsData.id_folha}
            />
          </Label>
        </Col>
        {fieldsData.vigencia && (
          <>
            <Col span={24}>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
              >
                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  lg={8}
                  xl={8}
                >
                  <Label label={<CustomLabel8>Nome</CustomLabel8>}>
                    <Input
                      value={fieldsData.nome}
                      placeholder="Inisira um nome para essa parametrização..."
                      onChange={(e) => {
                        setFieldsData({ ...fieldsData, nome: e.target.value });
                      }}
                      disabled={!fieldsData.id_folha}
                    />
                  </Label>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <Label label="Base Horas">
                    <CustomInputNumber
                      value={fieldsData.base_hora}
                      min={0}
                      max={999}
                      onChange={(value) => {
                        setFieldsData({
                          ...fieldsData,
                          base_hora: value,
                        });
                      }}
                      disabled={!fieldsData.id_folha}
                    />
                  </Label>
                </Col>
              </Row>
              <Can
                I="update"
                a="payroll.parameterization.sensitive"
              >
                <Row
                  gutter={[8, 4]}
                  type="flex"
                  align="middle"
                  style={{ marginTop: '16px' }}
                >
                  <Col
                    span={24}
                    style={{ marginTop: '16px' }}
                  >
                    <Row
                      gutter={[8, 4]}
                      type="flex"
                      align="middle"
                      className="emails-list-admin"
                    >
                      <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Label
                          label={
                            <div style={{ display: 'flex' }}>
                              <CustomLabel8>Emails para alerta ao Administrador</CustomLabel8>
                            </div>
                          }
                        >
                          <TagsEmail
                            emails={fieldsData.email_alerta_adm}
                            onChange={(em) => {
                              if (em) {
                                setFieldsData({ ...fieldsData, email_alerta_adm: em.join(';') });
                              }
                            }}
                            customValidation={onValidacaoEmailBwg}
                          />
                        </Label>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Can>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
                style={{ marginTop: '16px' }}
              >
                <Col
                  span={24}
                  style={{ marginTop: '16px' }}
                >
                  <Row
                    gutter={[8, 4]}
                    type="flex"
                    align="middle"
                    className="emails-list"
                  >
                    <Col
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Label
                        label={
                          <div style={{ display: 'flex' }}>
                            <CustomLabel8>Emails para alerta ao Facilitador</CustomLabel8>
                          </div>
                        }
                      >
                        <TagsEmail
                          emails={fieldsData.email_alerta}
                          onChange={(em) => {
                            if (em) {
                              setFieldsData({ ...fieldsData, email_alerta: em.join(';') });
                            }
                          }}
                        />
                      </Label>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
                style={{ marginTop: '16px' }}
              >
                <Col
                  span={24}
                  style={{ marginTop: '16px' }}
                >
                  <Row
                    gutter={[8, 4]}
                    type="flex"
                    align="middle"
                    className="emails-list"
                  >
                    <Col
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Label
                        label={
                          <div style={{ display: 'flex' }}>
                            <CustomLabel8>Emails para Faturamento ao Financeiro</CustomLabel8>
                          </div>
                        }
                      >
                        <TagsEmail
                          emails={fieldsData.email_alerta_financeiro}
                          customValidation={onValidacaoEmail}
                          onChange={(em) => {
                            if (em) {
                              setFieldsData({ ...fieldsData, email_alerta_financeiro: em.join(';') });
                            }
                          }}
                        />
                      </Label>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
                style={{ marginTop: '16px' }}
              >
                <Col span={24}>
                  <Checkbox
                    checked={fieldsData.autoTransmit !== false}
                    disabled={!canChangeAutoTransmit}
                    onChange={(e) => {
                      if (!canChangeAutoTransmit) {
                        return;
                      }
                      setFieldsData({ ...fieldsData, autoTransmit: e.target.checked });
                    }}
                  >
                    Envio automático para Apdata{' '}
                  </Checkbox>
                </Col>
              </Row>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
                style={{ marginTop: '16px' }}
              >
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={6}
                  xl={6}
                >
                  <Checkbox
                    checked={fieldsData.adiantamento_auto}
                    onChange={(e) => {
                      setFieldsData({
                        ...fieldsData,
                        adiantamento_auto: e.target.checked,
                        adiantamento_auto_valor: 0,
                        adiantamento_auto_id_tipo: 1,
                      });
                    }}
                  >
                    Adiantamento Automático?
                  </Checkbox>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={2}
                  xl={2}
                >
                  <Switch
                    checkedChildren="Valor"
                    unCheckedChildren="%"
                    checked={fieldsData.adiantamento_auto_id_tipo === 2}
                    disabled={!fieldsData.adiantamento_auto}
                    onChange={(chk) => {
                      setFieldsData({
                        ...fieldsData,
                        adiantamento_auto_id_tipo: chk ? 2 : 1,
                        adiantamento_auto_valor: 0,
                      });
                    }}
                  />
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  {fieldsData.adiantamento_auto_id_tipo === 2 ? (
                    <InputMoney
                      value={fieldsData.adiantamento_auto_valor}
                      disabled={!fieldsData.adiantamento_auto}
                      getValue={(value) => {
                        setFieldsData({
                          ...fieldsData,
                          adiantamento_auto_valor: value,
                        });
                      }}
                    />
                  ) : (
                    <CustomInputNumber
                      value={fieldsData.adiantamento_auto_valor}
                      min={0}
                      max={999}
                      onChange={(value) => {
                        setFieldsData({
                          ...fieldsData,
                          adiantamento_auto_valor: value,
                        });
                      }}
                      disabled={!fieldsData.adiantamento_auto}
                    />
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
              >
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={8}
                  xl={8}
                >
                  <Checkbox
                    checked={fieldsData.atual_comp_ap}
                    onChange={(e) => {
                      setFieldsData({
                        ...fieldsData,
                        atual_comp_ap: e.target.checked,
                        atual_comp_ap_qtd_dias: undefined,
                      });
                    }}
                  >
                    Atualização da Competência no Apontamento?
                  </Checkbox>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <Label label="Dia">
                    <CustomInputNumber
                      min={1}
                      max={31}
                      placeholder="DD"
                      value={fieldsData.atual_comp_ap_qtd_dias}
                      onChange={(value) => {
                        setFieldsData({
                          ...fieldsData,
                          atual_comp_ap_qtd_dias: value,
                        });
                      }}
                      disabled={!fieldsData.atual_comp_ap}
                    />
                  </Label>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
              >
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={8}
                  xl={8}
                >
                  <Checkbox
                    checked={fieldsData.apresentar_coop_afast}
                    onChange={(e) => {
                      setFieldsData({
                        ...fieldsData,
                        apresentar_coop_afast: e.target.checked,
                        apresentar_coop_afast_competencias: 0,
                      });
                    }}
                  >
                    Apresentar Cooperados Afastados?
                  </Checkbox>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <Label label="Período">
                    <CustomSelect
                      placeholder="Selecione uma opção..."
                      value={fieldsData.apresentar_coop_afast_competencias}
                      disabled={!fieldsData.apresentar_coop_afast}
                      onChange={(value) => {
                        setFieldsData({
                          ...fieldsData,
                          apresentar_coop_afast_competencias: value,
                        });
                      }}
                    >
                      {AFASTADOS_COMPETENCIAS_OPTIONS.map((option) => {
                        const isRestricted = option.value > 4;
                        if (isRestricted && !userAccount.isAdmin) {
                          return null;
                        }

                        return (
                          <Option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </Option>
                        );
                      })}
                    </CustomSelect>
                  </Label>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Row
                gutter={[8, 4]}
                type="flex"
                align="middle"
              >
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={8}
                  xl={8}
                >
                  <Checkbox
                    checked={fieldsData.alerta_pedido_sup_inf}
                    onChange={(e) => {
                      setFieldsData({
                        ...fieldsData,
                        alerta_pedido_sup_inf: e.target.checked,
                        alerta_pedido_sup_inf_valor: 0,
                      });
                    }}
                  >
                    <CustomText>
                      Alertar quando o total de rendimentos for x% superior em relação à remuneração base
                    </CustomText>
                  </Checkbox>
                </Col>

                <Col
                  xs={24}
                  sm={24}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <Label label="Porcentagem">
                    <InputMoney
                      prefix="%"
                      value={fieldsData.alerta_pedido_sup_inf_valor}
                      disabled={!fieldsData.alerta_pedido_sup_inf}
                      getValue={(value) => {
                        setFieldsData({
                          ...fieldsData,
                          alerta_pedido_sup_inf_valor: value,
                        });
                      }}
                    />
                  </Label>
                </Col>
              </Row>
            </Col>

            <Col
              span={24}
              style={{ marginTop: '16px' }}
            >
              <Row
                gutter={[8, 32]}
                type="flex"
                align="middle"
              >
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={12}
                  xl={12}
                >
                  <Checkbox
                    checked={fieldsData.mensa_propor_remu_admissao}
                    onChange={(e) => {
                      setFieldsData({
                        ...fieldsData,
                        mensa_propor_remu_admissao: e.target.checked,
                      });
                    }}
                  >
                    Mensalista Proporcionaliza Remuneração Na Adesão?
                  </Checkbox>
                </Col>
                {/* <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                <Checkbox
                  checked={fieldsData.horis_propor_remu_admissao}
                  onChange={(e) => {
                    setFieldsData({
                      ...fieldsData,
                      horis_propor_remu_admissao: e.target.checked,
                    });
                  }}

                >
                  Horista Proporcionaliza Remuneração Na Adesão?
                </Checkbox>
              </Col>
              */}
              </Row>
              {/* TODO Refazer estrutura de Rows e Col da tela */}
              <Row
                gutter={[8, 32]}
                type="flex"
                align="middle"
                style={{ marginTop: '24px' }}
              >
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={12}
                  xl={12}
                >
                  <Checkbox
                    checked={fieldsData.exibe_param_apontamento}
                    onChange={(e) => {
                      setFieldsData({
                        ...fieldsData,
                        exibe_param_apontamento: e.target.checked,
                      });
                    }}
                  >
                    Exibe parametrizações no apontamento?
                  </Checkbox>
                </Col>
              </Row>
              <Checkbox
                style={{ marginTop: '24px' }}
                disabled={isApiIntegrationEnabled || !canUpdateSensitiveParam}
                checked={isApiIntegrationEnabled ? false : fieldsData.ga_should_sync_earning_values}
                onChange={(e) => {
                  if (isApiIntegrationEnabled) {
                    return;
                  }
                  setFieldsData({
                    ...fieldsData,
                    ga_should_sync_earning_values: e.target.checked,
                  });
                }}
              >
                Preencher campos &quot;Valor Hora&quot; e &quot;Remuneração Base&quot; automaticamente com dados do
                Apdata{' '}
                <Tooltip
                  title="Selecionando essa opção, os campos terão seus valores substituídos pelos que estão
                    cadastrados no Apdata e também impedirá a edição dos valores da coluna.
                    Certifique-se que os valores cadastrados no Apdata estejam atualizados.
                    (Será aplicado somente na criação de novas requisições, não aferatá requisições existentes)"
                >
                  <InfoCircleFilled style={{ fontSize: '9px' }} />
                </Tooltip>
              </Checkbox>
              {isApiIntegrationEnabled && (
                <Alert
                  style={{ marginTop: '8px' }}
                  type="info"
                  showIcon
                  message={API_INTEGRATION_DISABLES_APDATA_SYNC_MESSAGE}
                />
              )}
              <Can
                I="update"
                a="payroll.parameterization.block-contract"
              >
                <Row
                  gutter={[8, 32]}
                  type="flex"
                  align="middle"
                  style={{ marginTop: '24px' }}
                >
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={12}
                  >
                    <Checkbox
                      checked={fieldsData.bloquear_coop_sem_contrato}
                      onChange={(e) => {
                        setFieldsData({
                          ...fieldsData,
                          bloquear_coop_sem_contrato: e.target.checked,
                        });
                      }}
                    >
                      Bloquear Cooperados sem Contrato Assinado
                    </Checkbox>
                  </Col>
                </Row>
              </Can>
            </Col>
          </>
        )}
      </Row>
      <Row
        type="flex"
        justify="end"
        style={{ marginTop: '16px' }}
      >
        <Col>
          <Button
            className="salvar-parametrizacao"
            type="primary"
            onClick={salvar}
            loading={loading}
          >
            <CustomText
              fontSize="12px"
              color="#f6f7f9"
            >
              {fieldsData.id === 0 ? 'Salvar' : 'Alterar'}
            </CustomText>
          </Button>
        </Col>
      </Row>
    </Page>
  );
}

export default ParametrizacaoApp;
